import { useEffect, useState } from "react";
import { api } from "../../../api/apiClient";
import type { WebOrderJourney } from "../../../api/types";
import { useDismissOnEscape } from "../../../hooks/useDismissOnEscape";
import { euro } from "../../finances/opcostFormat";
import { marginText } from "./marginText";
import { OrderNumberLink } from "./OrderNumberLink";
import { STATE_BADGE, linesText, stepDetail, stepTitle, whenText } from "./webOrdersText";

/**
 * G-143 — one web order's journey: who accepted, built, handed over and
 * carried it, and its money. The shell is the stock list's `.inv-sheet`
 * (a bottom sheet on a phone); `wo-journey` docks it to the right on a
 * desktop. Cost rows appear only when the server sent cost (owners, OD-220).
 */
export function OrderJourneySheet({ orderId, onClose }: { orderId: string; onClose: () => void }) {
  useDismissOnEscape(onClose);
  const [journey, setJourney] = useState<WebOrderJourney | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setJourney(null);
    setError(null);
    api
      .getWebOrderJourney(orderId)
      .then((j) => {
        if (!cancelled) setJourney(j);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : "Could not load the order");
      });
    return () => {
      cancelled = true;
    };
  }, [orderId]);

  const badge = journey ? STATE_BADGE[journey.state] : null;
  const money = journey?.money ?? null;

  return (
    <div
      className="inv-sheet-backdrop wo-journey-backdrop"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="inv-sheet wo-journey"
        role="dialog"
        aria-modal="true"
        aria-label={journey ? `Web order ${journey.orderNumber ?? ""}` : "Web order"}
      >
        <div className="wo-journey-head">
          <div>
            <div className="inv-sheet-title">
              Web order {journey ? <OrderNumberLink row={journey} /> : null}
            </div>
            {journey ? (
              <div className="inv-sheet-sub">
                {whenText(journey.arrivedAt)} · {linesText(journey)}
              </div>
            ) : null}
          </div>
          <button type="button" className="inv-sheet-close" onClick={onClose}>
            Close
          </button>
        </div>

        {error ? <div className="alert error">{error}</div> : null}
        {!journey && !error ? <p className="muted">Loading...</p> : null}

        {journey && badge ? (
          <>
            <span className={`${badge.className} wo-journey-state`}>
              {badge.label}
              {journey.shop ? ` · ${journey.shop.name}` : ""}
            </span>

            {journey.cannotBeMade ? (
              <section className="wo-journey-section" aria-labelledby="wo-journey-why">
                <h3 id="wo-journey-why">Why</h3>
                {journey.cannotBeMade.pickupIssue ? <p>{journey.cannotBeMade.pickupIssue}</p> : null}
                {journey.cannotBeMade.missing.length ? (
                  <ul>
                    {journey.cannotBeMade.missing.map((text) => (
                      <li key={text}>{text}</li>
                    ))}
                  </ul>
                ) : !journey.cannotBeMade.pickupIssue ? (
                  <p className="muted">Everything is in stock now</p>
                ) : null}
              </section>
            ) : null}

            <section className="wo-journey-section" aria-labelledby="wo-journey-steps">
              <h3 id="wo-journey-steps">What happened</h3>
              <ol className="wo-steps">
                {journey.steps.map((step, index) => (
                  <li key={`${step.kind}-${index}`} data-kind={step.kind}>
                    <strong>{stepTitle(step)}</strong>
                    <span className="muted">
                      {stepDetail(step)}
                      {step.kind === "DELIVERED" && journey.hasProofPhoto ? " · photo at the door" : ""}
                    </span>
                  </li>
                ))}
              </ol>
            </section>

            {journey.bouquets.length ? (
              <section className="wo-journey-section" aria-labelledby="wo-journey-bouquets">
                <h3 id="wo-journey-bouquets">In the bouquets</h3>
                {journey.bouquets.map((b, index) => (
                  <div key={`${b.title}-${index}`} className="wo-bouquet">
                    <span className="muted">
                      {b.title} · built by {b.builtBy.fullName} · {whenText(b.builtAt)}
                    </span>
                    <dl className="an-chain">
                      {b.stems.map((stem, stemIndex) => (
                        <div key={`${stem.name}-${stemIndex}`} className="an-chain-row">
                          <dt>
                            {Number(stem.quantity)} × {stem.name}
                          </dt>
                          <dd>{stem.costEur === undefined ? "" : stem.costEur === null ? "unknown" : euro(stem.costEur)}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                ))}
              </section>
            ) : null}

            <section className="wo-journey-section" aria-labelledby="wo-journey-money">
              <h3 id="wo-journey-money">Money</h3>
              <dl className="an-chain">
                <div className="an-chain-row">
                  <dt>Customer paid in Shopify</dt>
                  <dd>{euro(journey.customerPaidEur)}</dd>
                </div>
                {Number(journey.refundedInShopifyEur) > 0 ? (
                  <div className="an-chain-row">
                    <dt>Refunded in Shopify</dt>
                    <dd>{euro(journey.refundedInShopifyEur)}</dd>
                  </div>
                ) : null}
                {money ? (
                  <>
                    <div className="an-chain-row">
                      <dt>Booked as sale{journey.handedOverBy ? ` · ${journey.handedOverBy.fullName}` : ""}</dt>
                      <dd>{euro(money.bookedEur)}</dd>
                    </div>
                    <div className="an-chain-row">
                      <dt>Goods IVA</dt>
                      <dd>{euro(money.vatEur)}</dd>
                    </div>
                    {money.costEur !== undefined ? (
                      <div className="an-chain-row">
                        <dt>Cost of goods</dt>
                        <dd>{euro(money.costEur)}</dd>
                      </div>
                    ) : null}
                    <div className="an-chain-row is-total">
                      <dt>Margin</dt>
                      <dd>{marginText(money)}</dd>
                    </div>
                    <div className="an-chain-row">
                      <dt>Commission</dt>
                      <dd>{euro(money.commissionEur)}</dd>
                    </div>
                  </>
                ) : (
                  <div className="an-chain-row">
                    <dt>Booked as sale</dt>
                    <dd className="muted">Not handed over</dd>
                  </div>
                )}
                {journey.courierPayEur !== null ? (
                  <div className="an-chain-row">
                    <dt>Courier payout{journey.courier ? ` · ${journey.courier.fullName}` : ""}</dt>
                    <dd>{euro(journey.courierPayEur)}</dd>
                  </div>
                ) : null}
              </dl>
            </section>
          </>
        ) : null}
      </div>
    </div>
  );
}
